import { Capacitor } from "@capacitor/core";
import { isNative, platform } from "@/lib/native";
import { api } from "@/lib/api";

const ASKED_KEY = "auk_push_asked";
const DEVICE_KEY = "auk_push_device_id";
const ALLOWED = ["/marketplace", "/vender", "/minhas-reservas", "/restaurantes", "/restaurante/", "/eventos", "/notificacoes", "/mensagens", "/admin", "/perfil", "/comunidade", "/negocios", "/empregos", "/classificados"];

let listeners = [];

async function plugin() {
  const { PushNotifications } = await import("@capacitor/push-notifications");
  return PushNotifications;
}

async function deviceInfo() {
  try {
    const { Device } = await import("@capacitor/device");
    const [{ identifier }, info] = await Promise.all([Device.getId(), Device.getInfo()]);
    localStorage.setItem(DEVICE_KEY, identifier);
    return { device_id: identifier, device_name: `${info.manufacturer || ""} ${info.model || ""}`.trim() || platform, os_version: info.osVersion };
  } catch { return { device_id: localStorage.getItem(DEVICE_KEY) || `web-${Math.random().toString(36).slice(2)}` }; }
}

export const pushSupported = isNative && Capacitor.isPluginAvailable("PushNotifications");
export const pushAsked = () => localStorage.getItem(ASKED_KEY) === "1";

export async function pushPermission() {
  if (!pushSupported) return "unsupported";
  try { return (await (await plugin()).checkPermissions()).receive; } catch { return "unsupported"; }
}

export function safePath(path, params) {
  if (typeof path !== "string" || !path.startsWith("/") || path.startsWith("//") || !ALLOWED.some(p => path.startsWith(p))) return "/notificacoes";
  let q = {};
  try { q = typeof params === "string" ? JSON.parse(params || "{}") : (params || {}); } catch {}
  const qs = new URLSearchParams(q).toString();
  return qs ? `${path}?${qs}` : path;
}

export async function initPush({ userId, navigate, onForeground }) {
  if (!pushSupported) return;
  const P = await plugin();
  await teardownPush();
  listeners.push(await P.addListener("registration", async ({ value }) => {
    const info = await deviceInfo();
    api.post("/push/devices", { token: value, platform, ...info, tz: Intl.DateTimeFormat().resolvedOptions().timeZone }).catch(() => {});
  }));
  listeners.push(await P.addListener("registrationError", (e) => console.warn("push registration error", e)));
  listeners.push(await P.addListener("pushNotificationReceived", (n) => { onForeground?.(n); }));
  listeners.push(await P.addListener("pushNotificationActionPerformed", ({ notification }) => {
    const d = notification?.data || {};
    if (d.uid && d.uid !== userId) return;
    if (d.nid) api.post(`/notifications/${d.nid}/read`).catch(() => {});
    navigate(safePath(d.path, d.params));
    P.removeAllDeliveredNotifications().catch(() => {});
  }));
  if (platform === "android") {
    try { await P.createChannel({ id: "angolauk_default", name: "ANGOLAUK+", description: "Encomendas, reservas, orçamentos e avaliações", importance: 4, visibility: 1, vibration: true }); } catch {}
    try { await P.createChannel({ id: "angolauk_quiet", name: "ANGOLAUK+ (silencioso)", importance: 2 }); } catch {}
  }
  if ((await P.checkPermissions()).receive === "granted") await P.register();
}

export async function requestPushPermission() {
  localStorage.setItem(ASKED_KEY, "1");
  if (!pushSupported) return "unsupported";
  const P = await plugin();
  const res = await P.requestPermissions();
  if (res.receive === "granted") await P.register();
  return res.receive;
}

export async function revokeThisDevice() {
  const id = localStorage.getItem(DEVICE_KEY);
  if (id) await api.delete(`/push/devices/${id}`).catch(() => {});
}

export async function teardownPush() {
  for (const l of listeners) { try { await l.remove(); } catch {} }
  listeners = [];
}
